import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Snackbar,
  Alert
} from '@mui/material';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, setDoc, getDoc} from 'firebase/firestore';
import { db } from '../../firebase';
import { textFieldStyles } from '../../Login/LoginPageStyles';

// Utility Functions
const calculateWorkHours = (existingWorkHours, startTime, endTime) => {
  // Parse the existing work hours
  const [existingHoursStr, existingMinutesStr] = existingWorkHours.split(' ').map(Number);
  const existingHours = isNaN(existingHoursStr) ? 0 : existingHoursStr;
  const existingMinutes = isNaN(existingMinutesStr) ? 0 : existingMinutesStr;

  // Parse the start and end time
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  const start = new Date();
  const end = new Date();

  // Set the start and end times
  start.setHours(startHour, startMinute);
  end.setHours(endHour, endMinute);

  // Calculate the difference in milliseconds
  const diffInMs = end - start;

  // Convert milliseconds to minutes
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const newHours = Math.floor(diffInMinutes / 60);
  const newMinutes = diffInMinutes % 60;

  // Add the new hours and minutes to the existing ones
  const totalMinutes = existingMinutes + newMinutes;
  const additionalHours = Math.floor(totalMinutes / 60);
  const updatedMinutes = totalMinutes % 60;
  const updatedHours = existingHours + newHours + additionalHours;

  return { updatedHours, updatedMinutes };
};

const formatDate = (date) => date.toISOString().split('T')[0]; // Format date to YYYY-MM-DD

const calculateTimeDifference = (startTime, endTime) => {
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  const start = new Date();
  const end = new Date();

  start.setHours(startHour, startMinute);
  end.setHours(endHour, endMinute);

  const diffInMs = end - start;
  return Math.floor(diffInMs / (1000 * 60)); // Return difference in minutes
};

const convertToHours = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} hours ${remainingMinutes} minutes`;
};

const TaskTimesheetDialog = ({ open, onClose, task, user }) => {
  const [workDone, setWorkDone] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [date, setDate] = useState('');
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [totalWorkHours, setTotalWorkHours] = useState('0 0'); // Default: "0 hours 0 minutes"
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  //logic for date selection
  useEffect(() => {
    const calculateDateRange = () => {
      const joiningDate = new Date(user.joiningDate); // Employee's joining date
      const today = new Date();
      const paydayDate = new Date(today.getFullYear(), today.getMonth(), joiningDate.getDate());
  
      let startDate, endDate;
  
      if (today > paydayDate) {
        // Current date is after the payday date
        startDate = new Date(paydayDate.getFullYear(), paydayDate.getMonth(), joiningDate.getDate() +1); // Start of the current month
        endDate = new Date(paydayDate.getFullYear(), paydayDate.getMonth() + 1, joiningDate.getDate()); // End of the current month
      } else {
        // Current date is before or on the payday date
        startDate = new Date(paydayDate.getFullYear(), paydayDate.getMonth() - 1, joiningDate.getDate() +1); // Start of the previous month
        endDate = new Date(paydayDate.getFullYear(), paydayDate.getMonth(), joiningDate.getDate()); // End of the previous month
      }
  
      // Format dates as YYYY-MM-DD
      setMinDate(startDate.toISOString().split('T')[0]);
      setMaxDate(endDate.toISOString().split('T')[0]);
  
      console.log("Start of month:", startDate.toISOString());
      console.log("End of month:", endDate.toISOString());
    };
  
    calculateDateRange();
  }, [user.joiningDate]);
  
  

// Calculate monthly hours
const handleCalculate = useCallback(async () => {
  try {
    const joiningDate = new Date(user.joiningDate);
    const currentMonthDate = new Date();
    const paydayDate = new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth(), joiningDate.getDate());

    // Calculate for the current month
    const startOfMonth = new Date(paydayDate.getFullYear(), paydayDate.getMonth() - 1, paydayDate.getDate());
    const endOfMonth = new Date(paydayDate);
    endOfMonth.setDate(paydayDate.getDate() - 1);

    // Define a function to handle the querying and updating logic
    const processMonth = async (start, end) => {
      const tasksTimesheetRef = collection(db, 'tasksTimesheet');
      const q = query(
        tasksTimesheetRef,
        where('employeeId', '==', user.id),
        where('date', '>=', formatDate(start)),
        where('date', '<=', formatDate(end))
      );
      const querySnapshot = await getDocs(q);

      let totalMinutes = 0;

      querySnapshot.forEach((doc) => {
        const { startTime, endTime, updatedHours } = doc.data();
        if (startTime && endTime) {
          const timeDiff = calculateTimeDifference(startTime, endTime);
          totalMinutes += timeDiff;
        } else if (updatedHours) {
          const hoursInMinutes = updatedHours * 60;
          totalMinutes += hoursInMinutes;
        }
      });

      const monthlyHoursNumeric = convertToHours(totalMinutes);

      // Format the month range like "5 August - 5 September 2024"
      const formattedMonthLabel = `${start.getDate()} ${start.toLocaleString('default', { month: 'long' })} - ${end.getDate()} ${end.toLocaleString('default', { month: 'long', year: 'numeric' })}`;

      // Fetch the existing document to check the current monthly hours
      const existingDocRef = doc(db, `employeeMonthlyHours/${user.id}-${formattedMonthLabel}`);
      const existingDocSnapshot = await getDoc(existingDocRef);

      // Only update the document if the monthly hours have changed
      if (!existingDocSnapshot.exists() || existingDocSnapshot.data().monthlyHours !== monthlyHoursNumeric) {
        await setDoc(existingDocRef, {
          employeeId: user.id,
          month: formattedMonthLabel,
          monthlyHours: monthlyHoursNumeric,
          timestamp: new Date().toISOString(),
        }, { merge: true });
      }
    };

    if (currentMonthDate > paydayDate) {
      // Current date is after the payday date
      // Process current month
      await processMonth(startOfMonth, endOfMonth);

      // Process next month
      const nextStartOfMonth = paydayDate;
      const nextEndOfMonth = new Date(paydayDate.getFullYear(), paydayDate.getMonth() + 1, paydayDate.getDate() - 1);
      await processMonth(nextStartOfMonth, nextEndOfMonth);
    } else {
      // Current date is before or on the payday date
      await processMonth(startOfMonth, endOfMonth);
    }

    setSnackbarMessage('Entry added and Monthly hours updated successfully!');
    setSnackbarOpen(true);
  } catch (error) {
    console.error("Error calculating monthly hours: ", error);
    setSnackbarMessage('Error calculating monthly hours.');
    setSnackbarOpen(true);
  }
}, [user.id, user.joiningDate]);





  useEffect(() => {
    const fetchTimesheetEntries = async () => {
      try {
        if (task.id) {
          const q = query(
            collection(db, 'tasksTimesheet'),
            where('taskId', '==', task.id)
          );
          const querySnapshot = await getDocs(q);
          const entries = querySnapshot.docs.map(doc => doc.data());
          setTimesheetEntries(entries);

          // Calculate total work hours from existing entries
          const initialTotalHours = entries.reduce((acc, entry) => {
            const { updatedHours, updatedMinutes } = calculateWorkHours(
              acc,
              entry.startTime,
              entry.endTime
            );
            return `${updatedHours} ${updatedMinutes}`;
          }, '0 0');
          setTotalWorkHours(initialTotalHours);
        }
      } catch (error) {
        console.error('Error fetching timesheet entries: ', error);
      }
    };

    if (open) {
      fetchTimesheetEntries();
    }
  }, [open, task.id]);

  const handleSaveTimesheetEntry = async () => {
    // Check if all required fields are filled
    if (!date || !startTime || !endTime || !workDone) {
      setSnackbarMessage('Please fill in all fields.');
      setSnackbarOpen(true);
      return; // Exit the function if validation fails
    }
  
    try {
      const { updatedHours, updatedMinutes } = calculateWorkHours(
        totalWorkHours,
        startTime,
        endTime
      );
  
      const timestamp = new Date(); // Get the current timestamp
  
      const newEntry = {
        taskId: task.id,
        taskName: task.taskName,
        date,
        startTime,
        endTime,
        workDone,
        updatedHours,
        employeeId: user.id,
        employeeName: user.name, // Assuming you have the user's name in the user object
        timestamp: timestamp.toISOString() // Store timestamp in ISO format
      };
  
      // Save the new entry to Firestore as a new document
      await addDoc(collection(db, 'tasksTimesheet'), newEntry);
  
      // Update the total work hours
      setTotalWorkHours(`${updatedHours} ${updatedMinutes}`);
  
      // Update the assignedTasks collection with new work hours
      const taskRef = doc(db, 'assignedTasks', task.id);
      await updateDoc(taskRef, {
        workHours: `${updatedHours} hours ${updatedMinutes} minutes`,
        status: 'ongoing'
      });
  
      // Update local timesheet entries state
      setTimesheetEntries([...timesheetEntries, newEntry]);
  
      // Clear input fields
      setWorkDone('');
      setStartTime('');
      setEndTime('');
      setDate('');
  
      // Calculate monthly hours
      await handleCalculate();
  
      // Success message
      setSnackbarMessage('Entry added and Monthly hours updated successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving timesheet entry: ', error);
      setSnackbarMessage('Error saving timesheet entry.');
      setSnackbarOpen(true);
    }
  };
  

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Timesheet for Task ID: {task.taskId}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{mt:2}}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Task ID"
              value={task.taskId}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
              sx={{ ...textFieldStyles }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Task"
              value={task.taskName}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
              sx={{ ...textFieldStyles }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Assigned By"
              value={task.assignedBy}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
              sx={{ ...textFieldStyles }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: minDate,
                max: maxDate,
              }}
              sx={{ ...textFieldStyles }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Start Time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ ...textFieldStyles }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="End Time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ ...textFieldStyles }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Work Done"
              value={workDone}
              onChange={(e) => setWorkDone(e.target.value)}
              fullWidth
              multiline
              rows={4}
              sx={{ ...textFieldStyles }}
            />
          </Grid>
        </Grid>
        <TableContainer component={Paper} elevation={0} sx={{ marginTop: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><b>Task ID</b></TableCell>
                <TableCell><b>Date</b></TableCell>
                <TableCell><b>Start Time</b></TableCell>
                <TableCell><b>End Time</b></TableCell>
                <TableCell sx={{width: 300}}><b>Work Done</b></TableCell>
                <TableCell sx={{width: 300}}><b>Comments</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timesheetEntries
                .sort((a, b) => new Date(b.date) - new Date(a.date)) 
                .map((entry, index) => (
                <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? '#f5f5f5' : '#ffffff' }} >
                  <TableCell>{entry.taskId}</TableCell>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell>{entry.startTime}</TableCell>
                  <TableCell>{entry.endTime}</TableCell>
                  <TableCell>{entry.workDone}</TableCell>
                  <TableCell>{entry.comment}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: 'red' }}>
          Close
        </Button>
        <Button onClick={handleSaveTimesheetEntry} variant="contained" 
        sx={{ backgroundColor: '#39B54A', '&:hover': { backgroundColor: '#2c8f39' } }}>
          Save Entry
        </Button>
      </DialogActions>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default TaskTimesheetDialog;
